import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError, setAccessToken } from "@/api/client";
import { useBrand } from "@/brand/BrandProvider";
import { Badge, Button, Field, Input, tx } from "@/components/ui";

type Me = { first_name: string; last_name: string; phone: string; email: string | null; status: string; can_book: boolean; cutoff_hours: number; intake_completed: boolean; intake_required: boolean; brand: { slug: string; name: Record<string, string> } };
type MyAppt = { id: string; starts_at: string; ends_at: string; status: string; service: Record<string, string> | null; store: Record<string, string> | null; tz: string; price_quoted: string; can_change_free: boolean; training_minutes: number | null };
type Intake = { schema: { key: string; type: string; label: Record<string, string>; required?: boolean; contra?: boolean }[]; completed: boolean; answers: Record<string, unknown> | null };

export function CustomerArea() {
  const { t, i18n } = useTranslation();
  const brand = useBrand();
  const qc = useQueryClient();
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"identify" | "code" | "in">("identify");
  const [error, setError] = useState<string | null>(null);
  const [debug, setDebug] = useState<string | null>(null);
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/me"), enabled: stage === "in", retry: false });
  useEffect(() => { api<Me>("/me").then(() => setStage("in")).catch(() => undefined); }, []);
  const request = useMutation({ mutationFn: () => api<{ sent: boolean; channel: string; debug_code?: string }>("/me/otp/request", { method: "POST", body: JSON.stringify({ identifier }) }), onSuccess: (r) => { setStage("code"); setDebug(r.debug_code ?? null); setError(null); }, onError: (e) => setError(e instanceof ApiError && e.status === 404 ? t("me.unknown") : t("app.error")) });
  const verify = useMutation({ mutationFn: () => api<{ access_token: string }>("/me/otp/verify", { method: "POST", body: JSON.stringify({ identifier, code }) }), onSuccess: (r) => { setAccessToken(r.access_token); setStage("in"); setError(null); }, onError: () => setError(t("me.badCode")) });
  const logout = async () => { try { await api("/auth/logout", { method: "POST" }); } finally { setAccessToken(null); setStage("identify"); qc.clear(); } };
  const title = brand ? tx(brand.name, i18n.language) : t("app.title");
  return (
    <main className="min-h-screen p-4 flex justify-center">
      <div className="w-full max-w-lg space-y-4">
        <header className="flex items-center justify-between"><h1 className="text-xl font-semibold text-accent">{title}</h1>
          <div className="flex gap-3 text-xs"><button className="underline" onClick={() => i18n.changeLanguage(i18n.language === "el" ? "en" : "el")}>{i18n.language === "el" ? "EN" : "EL"}</button>{stage === "in" && <button className="underline" onClick={logout}>{t("auth.logout")}</button>}</div></header>
        {stage !== "in" && (
          <form onSubmit={(e: FormEvent) => { e.preventDefault(); stage === "identify" ? request.mutate() : verify.mutate(); }} className="rounded-2xl bg-surface shadow p-5 space-y-3">
            <p className="text-sm text-muted">{t("me.intro")}</p>
            <Field label={t("me.identifier")}><Input value={identifier} onChange={(e) => setIdentifier(e.target.value)} disabled={stage === "code"} required placeholder="69… / email" /></Field>
            {stage === "code" && <Field label={t("me.code")} hint={debug ? `${t("me.debugCode")}: ${debug}` : t("me.codeSent")}><Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" pattern="\d{6}" required autoFocus /></Field>}
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex gap-2"><Button disabled={request.isPending || verify.isPending}>{stage === "identify" ? t("me.sendCode") : t("auth.login")}</Button>{stage === "code" && <Button type="button" variant="ghost" onClick={() => setStage("identify")}>{t("app.change")}</Button>}</div>
          </form>
        )}
        {stage === "in" && me.data && <Account me={me.data} onIntake={() => me.refetch()} />}
      </div>
    </main>
  );
}

function Account({ me, onIntake }: { me: Me; onIntake: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [scope, setScope] = useState<"upcoming" | "past">("upcoming");
  const [error, setError] = useState<string | null>(null);
  const appts = useQuery({ queryKey: ["my-appts", scope], queryFn: () => api<MyAppt[]>(`/me/appointments?scope=${scope}`) });
  const cancel = useMutation({ mutationFn: (id: string) => api<{ penalty: boolean }>(`/me/appointments/${id}/cancel`, { method: "POST" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["my-appts"] }), onError: () => setError(t("app.error")) });
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-surface shadow p-5 space-y-1 text-sm">
        <div className="text-lg font-semibold">{me.first_name} {me.last_name}</div>
        <div className="text-muted">{me.phone}{me.email ? ` · ${me.email}` : ""}</div>
        <div><Badge tone={me.status === "confirmed" ? "green" : "amber"}>{t(`cstatus.${me.status}`)}</Badge> {!me.can_book && <span className="text-xs text-muted">{t("me.notYetBookable")}</span>}</div>
        <div className="text-xs text-muted">{t("me.cutoffInfo", { hours: me.cutoff_hours })}</div>
      </div>
      {me.intake_required && !me.intake_completed && <IntakeForm onDone={onIntake} />}
      <MyCredits />
      <MyRewards />
      <MyTreatments />
      <div className="rounded-2xl bg-surface shadow p-5 space-y-3">
        <div className="flex gap-2"><Button variant={scope === "upcoming" ? "primary" : "ghost"} onClick={() => setScope("upcoming")}>{t("me.upcoming")}</Button><Button variant={scope === "past" ? "primary" : "ghost"} onClick={() => setScope("past")}>{t("me.past")}</Button></div>
        {appts.data?.length === 0 && <p className="text-sm text-muted">{t("me.none")}</p>}
        <ul className="divide-y text-sm">{appts.data?.map((a) => (
          <li key={a.id} className="py-2 flex items-start justify-between gap-2">
            <div>
              <div className="font-medium">{new Date(a.starts_at).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short", timeZone: a.tz })}</div>
              <div className="text-muted">{a.service ? tx(a.service, i18n.language) : ""}{a.training_minutes ? ` · ${t("me.trainingLasts", { minutes: a.training_minutes })}` : ""}</div>
              <Badge tone={a.status === "scheduled" ? "blue" : a.status === "completed" ? "green" : "red"}>{t(`status.${a.status}`)}</Badge>
            </div>
            {a.status === "scheduled" && scope === "upcoming" && (
              <Button variant="ghost" onClick={() => confirm(a.can_change_free ? t("me.cancelConfirm") : t("me.cancelPenalty")) && cancel.mutate(a.id)}>{t("appt.cancel")}</Button>
            )}
          </li>))}</ul>
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </div>
  );
}

function IntakeForm({ onDone }: { onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const intake = useQuery({ queryKey: ["my-intake"], queryFn: () => api<Intake>("/me/intake") });
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = useMutation({ mutationFn: () => api("/me/intake", { method: "POST", body: JSON.stringify({ answers, signed_by_name: name }) }), onSuccess: onDone, onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")) });
  if (!intake.data) return null;
  return (
    <form onSubmit={(e) => { e.preventDefault(); submit.mutate(); }} className="rounded-2xl bg-surface shadow p-5 space-y-3 text-sm">
      <div className="font-semibold">{t("me.intakeTitle")}</div>
      <p className="text-muted">{t("me.intakeIntro")}</p>
      {intake.data.schema.map((q) => q.type === "bool"
        ? <label key={q.key} className="flex items-start gap-2"><input type="checkbox" checked={!!answers[q.key]} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.checked })} /> <span>{tx(q.label, i18n.language)}{q.required ? " *" : ""}</span></label>
        : <Field key={q.key} label={tx(q.label, i18n.language)}><Input value={String(answers[q.key] ?? "")} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })} /></Field>)}
      <Field label={t("me.signedBy")}><Input value={name} onChange={(e) => setName(e.target.value)} required /></Field>
      {error && <p className="text-danger">{error}</p>}
      <Button disabled={submit.isPending}>{t("me.submitIntake")}</Button>
    </form>
  );
}

type MyRw = { enabled: boolean; balance: number; value: string; point_value?: string; min_redeem?: number; ledger: { delta: number; reason: string; at: string; balance_after: number }[] };

/** Points balance and the last movements. */
function MyRewards() {
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ["my-rewards"], queryFn: () => api<MyRw>("/me/rewards") });
  if (!q.data?.enabled) return null;
  return (
    <div className="rounded-2xl bg-surface shadow p-5 space-y-2 text-sm">
      <div className="flex items-center justify-between"><div className="font-semibold">{t("me.points")}</div><Badge tone="accent">{q.data.balance}</Badge></div>
      <div className="text-xs text-muted">{t("me.pointsValue", { value: q.data.value })}{q.data.min_redeem ? ` · ${t("rw.minRedeemShort", { n: q.data.min_redeem })}` : ""}</div>
      <ul className="divide-y text-xs text-muted">{q.data.ledger.slice(0, 8).map((r, i) => <li key={i} className="flex justify-between py-1"><span>{t(`rw.reason.${r.reason}`, { defaultValue: r.reason })}</span><span className={r.delta < 0 ? "text-danger" : "text-ok"}>{r.delta > 0 ? `+${r.delta}` : r.delta}</span><span>{new Date(r.at).toLocaleDateString(i18n.language)}</span></li>)}</ul>
    </div>
  );
}

type MyTreat = { sessions: { id: string; at: string; service: Record<string, string> | null; session_no: number }[]; next_due: { service: Record<string, string> | null; due_on: string; last_at: string; sessions: number }[] };

/** Next suggested session per treatment and the sessions done so far (dates only). */
function MyTreatments() {
  const { t, i18n } = useTranslation();
  const q = useQuery({ queryKey: ["my-treatments"], queryFn: () => api<MyTreat>("/me/treatments") });
  if (!q.data || (q.data.sessions.length === 0 && q.data.next_due.length === 0)) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="rounded-2xl bg-surface shadow p-5 space-y-3 text-sm">
      <div className="font-semibold">{t("me.treatments")}</div>
      {q.data.next_due.map((n, i) => (
        <div key={i} className={`rounded-lg px-3 py-2 ${n.due_on < today ? "bg-warn-soft text-warn" : "bg-accent-soft text-accent-ink"}`}>
          <div className="font-medium">{n.service ? tx(n.service, i18n.language) : ""}</div>
          <div className="text-xs">{t("me.nextSuggested", { date: new Date(n.due_on).toLocaleDateString(i18n.language) })} · {t("me.sessionsDone", { n: n.sessions })}</div>
        </div>
      ))}
      <ul className="divide-y text-xs text-muted">{q.data.sessions.slice(0, 12).map((s) => <li key={s.id} className="flex justify-between py-1"><span>{s.service ? tx(s.service, i18n.language) : ""} #{s.session_no}</span><span>{new Date(s.at).toLocaleDateString(i18n.language)}</span></li>)}</ul>
    </div>
  );
}

type MyPkg = { id: string; package: { id: string; name: Record<string, string> }; credits_total: number; credits_left: number; expires_at: string; status: string };
type ShopPkg = { id: string; name: Record<string, string>; description: Record<string, string>; price: string; credits_paid: number; credits_bonus: number; validity_months: number };

function MyCredits() {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const mine = useQuery({ queryKey: ["my-credits"], queryFn: () => api<MyPkg[]>("/me/credits") });
  const shop = useQuery({ queryKey: ["shop-packages"], queryFn: () => api<ShopPkg[]>("/public/packages") });
  const buy = useMutation({
    mutationFn: (id: string) => api<{ checkout_url: string }>("/public/packages/buy", { method: "POST", body: JSON.stringify({ package_id: id }) }),
    onSuccess: (r) => { window.location.href = r.checkout_url; },
    onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")),
  });
  if (!mine.data?.length && !shop.data?.length) return null;
  return (
    <div className="rounded-2xl bg-surface shadow p-5 space-y-3 text-sm">
      <div className="font-semibold">{t("me.credits")}</div>
      {mine.data?.map((p) => (
        <div key={p.id} className="flex items-center justify-between rounded-lg bg-surface-2 px-3 py-2">
          <div><div className="font-medium">{tx(p.package.name, i18n.language)}</div><div className="text-xs text-muted">{t("me.expires", { date: new Date(p.expires_at).toLocaleDateString(i18n.language) })}</div></div>
          <Badge tone={p.status === "active" ? "green" : "gray"}>{p.credits_left}/{p.credits_total}</Badge>
        </div>
      ))}
      {shop.data && shop.data.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-faint">{t("me.buyPackage")}</div>
          {shop.data.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border px-3 py-2">
              <div><div className="font-medium">{tx(p.name, i18n.language)}</div><div className="text-xs text-muted">{p.credits_paid}{p.credits_bonus ? `+${p.credits_bonus}` : ""} · {p.validity_months} {t("me.months")} · {p.price}€</div></div>
              <Button type="button" variant="ghost" disabled={buy.isPending} onClick={() => buy.mutate(p.id)}>{t("me.buy")}</Button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-danger">{error}</p>}
    </div>
  );
}
